import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import { required } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import router from "@/router";
import axios from "@axios";
import {
  reactive,
  toRefs,
  onMounted,
  ref,
} from "@vue/composition-api";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { v4 as uuidv4 } from "uuid";
import { fieldStructure } from "@/components/operationalSecurityTemplateBuilder/config";
import { getUserData } from "@/auth/utils";
import { debounce } from "lodash";

export default function useTemplateBuilderConfig() {

  const state = reactive({
    items: [],
    isValid: false,
    templateId: null,
    isUpdating: false,
  });

  const templateName = ref("");
  const recipient = ref("");
  const idTenant = ref(null);
  const id_users_allowed_fill = ref([]);
  const id_group_allowed_fill = ref([]);
  const read_only_users = ref([]);
  const usersOptions = ref([]);
  const usersGroupOptions = ref([]);
  const prefillTemplate = ref(false);
  const requiresPrefill = ref(false);
  const isMailable = ref(false);
  const isMakerChecker = ref(false);
  const displayInAlarm = ref(false);
  const isHorizontal = ref(false);
  const isLoadingTemplate = ref(false);
  const relatedForms = ref([]);
  const allowDraft = ref(false);
  const relatedFormsOptions = ref([]);
  const toast = useToast();
  const { refFormObserver, getValidationState } = formValidation();
  const { t } = useI18nUtils();
  const trans = {
    'messages.edit_save.successfully_created': t('messages.edit_save.successfully_created'),
    'messages.edit_save.updated_successfully': t('messages.edit_save.updated_successfully'),
  };

  const addItem = () => {
    const item = JSON.parse(JSON.stringify(fieldStructure));
    item.item_id = uuidv4();
    state.items.push(item);
  };

  const save = async (extra = {}) => {
    const validation = await refFormObserver.value.validate();
    if (validation) {
      storeTemplate(
        templateName.value,
        state.items,
        extra //In this case just id_tenant
      );
    }
  };

  const storeTemplate = (name, data, extra = {}) => {
    const payload = {
      id_user: getUserData()?.id,
      name,
      data,
      id_users_allowed_fill: id_users_allowed_fill.value.map(u => u.id),
      id_group_allowed_fill: id_group_allowed_fill.value.map(u => u.id),
      read_only_users: read_only_users.value.map(u => u.id),
      prefill_template: prefillTemplate.value,
      requires_prefill: requiresPrefill.value,
      is_mailable: isMailable.value,
      is_maker_checker: isMakerChecker.value,
      display_in_alarm: displayInAlarm.value,
      is_horizontal: isHorizontal.value,
      related_to: relatedForms.value.map(u => u.id),
      allow_draft: allowDraft.value,
      notification_email: recipient.value,
      ...extra,
    };

    if (!state.isUpdating) {
      axios.post("v1/operational-security-forms/templates", payload).then(({ data }) => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['messages.edit_save.successfully_created'],
            icon: "CheckIcon",
            variant: "success",
          },
        });

        state.isUpdating = true;
        state.templateId = data.id;

        router.push({ path: `/operational_security/templates/builder/${state.templateId}` });
      }).catch((error)=> {
        const errorMessage = error?.response?.data?.message ?? "Error Saving Operational Form Template";
        toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    } else {
      axios.put(`v1/operational-security-forms/templates/${state.templateId}`, payload).then(({ data }) => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['messages.edit_save.updated_successfully'],
            icon: "CheckIcon",
            variant: "success",
          },
        });

        state.templateId = data.id;
        router.push({ path: `/operational_security/templates/builder/${state.templateId}` });
      });
    }
  };

  onMounted(async () => {
    getTemplate()
  });

  const getTemplate = async (items) => {
    try {
      if (router.currentRoute.params.id_template) {
        isLoadingTemplate.value = true;
        state.isUpdating = true;
        state.templateId = router.currentRoute.params.id_template;

        const { data } = await axios.get(`v1/operational-security-forms/templates/${state.templateId}`);
        const response = data;
        isLoadingTemplate.value = false;
        templateName.value = response?.name;
        recipient.value = response?.notification_email;
        idTenant.value = response?.tenant?.id;
        id_users_allowed_fill.value = response?.users_allowed_fill;
        id_group_allowed_fill.value = response?.groups_allowed_fill;
        read_only_users.value = response?.read_only_users ?? [];
        prefillTemplate.value = response?.prefill_template;
        requiresPrefill.value = response?.requires_prefill;
        isMailable.value = response?.is_mailable;
        isMakerChecker.value = response?.is_maker_checker;
        displayInAlarm.value = response?.display_in_alarm;
        isHorizontal.value = response?.is_horizontal;
        relatedForms.value = response?.related_templates;
        allowDraft.value = response?.allow_draft;
        if (response.data) {
          state.items = response.data;
          state.isValid = true;
        }
      }
    } catch(error) {
      isLoadingTemplate.value = false;
      const errorMessage = error?.response?.data?.message ?? "Error getting template";
      toast({
        component: ToastificationContent,
        props: {
          title: errorMessage,
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    }
  };

  const updateItems = (items) => {
    state.items = items;
  };

  const getUsers = async (q="") => {
    axios.get('/v1/users', {
      params: {
        q,
        max: 10,
        "page[n]": 1,
      },
    })
    .then(response => {
      // const options = response.data.data.map(user => ({
      //   ...user,
      //   fullName: `${user.name || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()
      // }));
      usersOptions.value = response.data.data.map(user => ({
        id: user.id,
        value: `${user.user || user.email} | ${user.full_name || ''}`.trim(),
        name: `${user.user || user.email} | ${user.full_name || ''}`.trim(),
      }))
    })
    .catch(() => {
      toast.error('Error');
    });
  };

  const getUsersGroup = async (q="") => {
    axios.get('/v1/groups', {
      params: {
        q,
        max: 10,
        "page[n]": 1,
      },
    })
    .then(response => {
      usersGroupOptions.value = response.data.data
    })
    .catch(() => {
      toast.error('Error');
    });
  };

  const getTemplatesRF = (q="") => {
    axios
      .get('/v1/operational-security-forms/templates', {
        params: {
          max: 10,
          q
        },
      })
      .then(response => {
        relatedFormsOptions.value = response?.data?.data;
      })
      .catch(() => {
        toast.error('Error');
      });
  };

  const onSearchUsers = debounce(function (search) {
    getUsers(search)
  }, 800);

  const onSearchUserGroup = debounce(function (search) {
    getUsersGroup(search)
  }, 800);

  const onSearchRelatedForms = debounce(function (search) {
    getTemplatesRF(search)
  }, 800);


  const userLabel = (user) => {
    return "name";
  };

  return {
    addItem,
    save,
    ...toRefs(state),
    templateName,
    recipient,
    refFormObserver,
    required,
    idTenant,
    id_users_allowed_fill,
    id_group_allowed_fill,
    read_only_users,
    usersOptions,
    usersGroupOptions,
    prefillTemplate,
    requiresPrefill,
    isMailable,
    isMakerChecker,
    displayInAlarm,
    isHorizontal,
    isLoadingTemplate,
    relatedFormsOptions,
    relatedForms,
    allowDraft,
    getValidationState,
    updateItems,
    onSearchUsers,
    onSearchUserGroup,
    onSearchRelatedForms,
    getUsersGroup,
    getUsers,
    userLabel,
    getTemplatesRF
  };
}
